import React, { useEffect } from "react";
import styles from "./FixrWidget.module.scss";
import FixrWidgetProps from "./FixrWidgetProps.interface";

import { FunctionComponent } from "react";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import Paragraph from "components/Paragraph/Paragraph";

const fixrWidget: FunctionComponent<FixrWidgetProps> = (props): JSX.Element => {
  const wrapperClasses = getWrapperClasses(props.settings);
  const wrapperStyles = useWrapperStyles(props.settings);

  const { splitClasses } = useSplitBackground(props);
  const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
  const headlineCaseClass = props.settings.headlineCase
    ? styles[`case_${props.settings.headlineCase}`]
    : undefined;

  function createInlineClasses(object: any, property: string, fn: Function) {
    if (property in object && object[property] !== "") {
      return fn(object[property]);
    }
    return "";
  }

  // Position
  const textPositionClass = createInlineClasses(
    props.settings,
    "fixrPosition",
    (value: any) => {
      switch (value) {
        case "Left":
          return styles.position_left;
        case "Center":
          return styles.position_center;
        case "Right":
          return styles.position_right;
        default:
          return null;
      }
    }
  );

  const attributes = props?.content?.fixrAttributesList?.reduce(
    (acc: any, attribute: any) => {
      acc[attribute?.content?.attributeText] = attribute?.content?.value;
      return acc;
    },
    {}
  );

  // Load script
  const loadScript = async (src: string) => {
    return new Promise<void>((resolve, _reject) => {
      const scriptElement = document.createElement("script");
      scriptElement.type = "text/javascript";
      scriptElement.setAttribute(
        "data-fixr-shop-id",
        `${props?.content?.fixrIdentifier}`
      );
      Object?.entries(attributes).forEach(([key, value]) => {
        scriptElement?.setAttribute(`${key}`, `${value}`);
      });
      scriptElement.async = false;
      scriptElement.onload = () => {
        resolve();
      };
      scriptElement.src = src;
      document.body
        .querySelector(`.${styles.fixr_container}`)
        ?.appendChild(scriptElement);
    });
  };

  useEffect(() => {
    loadScript(`${props?.content?.fixrScriptSrc}`);
    return () => {
      const scriptElement = document.querySelector(
        `script[data-fixr-shop-id="${props?.content?.fixrIdentifier}"]`
      );
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, []);

  return (
    <section
      className={combineClasses(
        styles.wrapper,
        textPositionClass,
        ...wrapperClasses
      )}
      style={{ ...wrapperStyles }}
    >
      <div
        className={combineClasses(styles.container_wrapper, textPositionClass)}
      >
        <div
          className={combineClasses(
            styles.container,
            ...splitClasses,
            inViewClasses
          )}
          ref={inViewElementRef}
        >
          <div className={styles.content}>
            {/* Headline */}
            {props.content.headline ? (
              <DynamicHeading
                key={`head_${props.contentUdi}`}
                className={combineClasses(styles.headline, headlineCaseClass)}
                text={props.content.headline}
                headlineSize={props.settings.headlineSize}
                headlineWeight={props.settings.headlineWeight}
              />
            ) : null}

            {/* Text */}
            {props.content.bodyText ? (
              <Paragraph
                key={`para_${props.contentUdi}`}
                className={styles.text}
                innerHTML={props.content.bodyText}
              />
            ) : null}
          </div>

          {/* Fixr container */}
          <div className={styles.fixr_container}>
            {/* Fixr script is appended here */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default fixrWidget;
